import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Mail, MapPin, Smartphone } from "lucide-react";
import { getCountries } from "react-phone-number-input";
import en from "react-phone-number-input/locale/en";
import { Input } from "../../tailwind/input";
import { Label } from "../../tailwind/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../tailwind/select";

const COUNTRIES = getCountries().map((code) => ({
  code,
  name: en[code] || code,
})).sort((a, b) => a.name.localeCompare(b.name));

const DEFAULT_COUNTRY = 'IN';

const buildFormState = (details) => ({
  legalName: details?.legalName || '',
  email: details?.email || '',
  phone: details?.phone || '',
  gstin: details?.gstin || '',
  address: {
    line1: details?.address?.line1 || '',
    line2: details?.address?.line2 || '',
    city: details?.address?.city || '',
    state: details?.address?.state || '',
    pinCode: details?.address?.pinCode || '',
    country: details?.address?.country || DEFAULT_COUNTRY,
  },
});

const FieldLabel = ({ children, required }) => (
  <Label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
    {children} {required && <span className="text-red-500 ml-1">*</span>}
  </Label>
);

export const BillingDetailsForm = forwardRef(({ initialBillingDetails, errors = {} }, ref) => {
  const [form, setForm] = useState(() => buildFormState(initialBillingDetails));

  useEffect(() => {
    setForm(buildFormState(initialBillingDetails));
  }, [initialBillingDetails]);

  useImperativeHandle(ref, () => ({
    getCurrentValues: () => form,
  }));

  const setField = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const setAddressField = (field) => (e) =>
    setForm((prev) => ({ ...prev, address: { ...prev.address, [field]: e.target.value } }));

  return (
    <>
      <div className="space-y-4">
        <div className="space-y-2">
          <FieldLabel required>Business Name</FieldLabel>
          <Input
            value={form.legalName}
            onChange={setField('legalName')}
            className={`h-11 dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:placeholder-gray-400 ${errors.legalName ? 'border-red-500' : ''}`}
            placeholder="Enter business name"
          />
          {errors.legalName && <p className="text-xs text-red-600 mt-1">{errors.legalName}</p>}
        </div>
        <div className="space-y-2">
          <FieldLabel required>Contact Email</FieldLabel>
          <div className="relative">
            <Mail className="absolute left-3 top-3.5 h-4 w-4 text-gray-400 dark:text-gray-500" />
            <Input
              value={form.email}
              onChange={setField('email')}
              className={`h-11 pl-9 dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:placeholder-gray-400 ${errors.email ? 'border-red-500' : ''}`}
              placeholder="Enter email address"
            />
            {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
          </div>
        </div>
        <div className="space-y-2">
          <FieldLabel>Phone Number</FieldLabel>
          <div className="relative">
            <Smartphone className="absolute left-3 top-3.5 h-4 w-4 text-gray-400 dark:text-gray-500" />
            <Input
              value={form.phone}
              onChange={setField('phone')}
              className={`h-11 pl-9 dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:placeholder-gray-400 ${errors.phone ? 'border-red-500' : ''}`}
              placeholder="Enter phone number"
            />
            {errors.phone && <p className="text-xs text-red-600 mt-1">{errors.phone}</p>}
          </div>
        </div>
        <div className="space-y-2">
          <FieldLabel>GSTIN (Optional)</FieldLabel>
          <Input
            value={form.gstin}
            onChange={setField('gstin')}
            className={`h-11 dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:placeholder-gray-400 ${errors.gstin ? 'border-red-500' : ''}`}
            placeholder="29ABCDE1234F1Z5"
          />
          {errors.gstin && <p className="text-xs text-red-600 mt-1">{errors.gstin}</p>}
          <p className="text-[10px] text-gray-400 dark:text-gray-500">Provide for B2B invoice. Leave blank for B2C.</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <FieldLabel required>Address Line 1</FieldLabel>
          <div className="relative">
            <MapPin className="absolute left-3 top-3.5 h-4 w-4 text-gray-400 dark:text-gray-500" />
            <Input
              value={form.address.line1}
              onChange={setAddressField('line1')}
              className={`h-11 pl-9 dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:placeholder-gray-400 ${errors['address.line1'] ? 'border-red-500' : ''}`}
              placeholder="Enter address line 1"
            />
            {errors['address.line1'] && <p className="text-xs text-red-600 mt-1">{errors['address.line1']}</p>}
          </div>
        </div>
        <div className="space-y-2">
          <FieldLabel>Address Line 2</FieldLabel>
          <Input
            value={form.address.line2}
            onChange={setAddressField('line2')}
            className="h-11 dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:placeholder-gray-400"
            placeholder="Enter address line 2 (optional)"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <FieldLabel required>City</FieldLabel>
            <Input
              value={form.address.city}
              onChange={setAddressField('city')}
              className={`h-11 dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:placeholder-gray-400 ${errors['address.city'] ? 'border-red-500' : ''}`}
              placeholder="Enter city"
            />
            {errors['address.city'] && <p className="text-xs text-red-600 mt-1">{errors['address.city']}</p>}
          </div>
          <div className="space-y-2">
              <FieldLabel required>State</FieldLabel>
            <Input
              value={form.address.state}
              onChange={setAddressField('state')}
                className={`h-11 dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:placeholder-gray-400 ${errors['address.state'] ? 'border-red-500' : ''}`}
              placeholder="Enter state"
            />
              {errors['address.state'] && <p className="text-xs text-red-600 mt-1">{errors['address.state']}</p>}
          </div>
          <div className="space-y-2">
              <FieldLabel required>Pin Code</FieldLabel>
            <Input
              value={form.address.pinCode}
              onChange={setAddressField('pinCode')}
                className={`h-11 dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:placeholder-gray-400 ${errors['address.pinCode'] ? 'border-red-500' : ''}`}
              placeholder="Enter pin code"
            />
              {errors['address.pinCode'] && <p className="text-xs text-red-600 mt-1">{errors['address.pinCode']}</p>}
            </div>
          <div className="space-y-2">
            <FieldLabel>Country</FieldLabel>
            <Select
              value={form.address.country || DEFAULT_COUNTRY}
              onValueChange={(val) =>
                setForm((prev) => ({ ...prev, address: { ...prev.address, country: val } }))
              }
            >
              <SelectTrigger className="h-11">
                <SelectValue placeholder="Select country" />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.map(({ code, name }) => (
                  <SelectItem key={code} value={code}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </>
  );
});